import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FieldArray, getIn, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { FormikDropdownField, FormikTelField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import InputLabel from '@components/InputLabel';

import { PHONE_USES } from '@models/patient';
import type { PhoneFormEntry, PhoneUse } from '@models/patient';

import { formatPhone } from '@utils/format';

/**
 * Row ids only have to be unique within one array, but a per-instance counter
 * restarts at 0 whenever the field unmounts (the creation form drops the whole
 * patient block when "Is this user a patient?" is unchecked) while Formik keeps
 * the rows, so the next push would reuse an id already on screen. Counting at
 * module scope means an id is never handed out twice.
 */
let nextEntryId = 0;

const messages = defineMessages({
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  phoneNumbers: {
    id: 'GLOBAL.phone.numbers',
    defaultMessage: 'Phone Numbers',
  },
  mobile: {
    id: 'GLOBAL.phone.mobile',
    defaultMessage: 'Mobile',
  },
  home: {
    id: 'GLOBAL.phone.home',
    defaultMessage: 'Home',
  },
  work: {
    id: 'GLOBAL.phone.work',
    defaultMessage: 'Work',
  },
  fax: {
    id: 'GLOBAL.phone.fax',
    defaultMessage: 'Fax',
  },
  addNumber: {
    id: 'GLOBAL.phone.addNumber',
    defaultMessage: 'Add another number',
  },
  primaryNumber: {
    id: 'GLOBAL.phone.primaryNumber',
    defaultMessage: 'Primary — phone number {position}',
  },
  removeNumber: {
    id: 'GLOBAL.phone.removeNumber',
    defaultMessage: 'Remove number',
  },
  phoneNumberWithPosition: {
    id: 'GLOBAL.phone.numberWithPosition',
    defaultMessage: 'Phone number {position}',
  },
  phoneType: {
    id: 'GLOBAL.phone.type',
    defaultMessage: 'Phone type',
  },
  phoneTypeWithPosition: {
    id: 'GLOBAL.phone.typeWithPosition',
    defaultMessage: 'Phone number {position} type',
  },
  primaryNumberGroupAriaLabel: {
    id: 'GLOBAL.phone.primaryNumberGroupAriaLabel',
    defaultMessage: 'Primary phone number',
  },
  readOnlyEntry: {
    id: 'GLOBAL.phone.readOnlyEntry',
    defaultMessage: '{number} ({type})',
  },
  readOnlyPrimaryEntry: {
    id: 'GLOBAL.phone.readOnlyPrimaryEntry',
    defaultMessage: '{number} ({type}, primary)',
  },
});

const phoneUseMessages: Record<
  PhoneUse,
  { id: string; defaultMessage: string }
> = {
  mobile: messages.mobile,
  home: messages.home,
  work: messages.work,
  fax: messages.fax,
};

interface PhoneNumbersFieldProps {
  isEditing: boolean;
  /**
   * Formik path the entry array lives at. Defaults to a top-level `phones`
   * for the patient profile form; the patient creation forms nest one array
   * per member, e.g. `members.0.phones`.
   */
  name?: string;
  /** Renders the number inputs full width. */
  fullWidth?: boolean;
  /**
   * Marks the number inputs required. Formik's `<Form>` doesn't set
   * `noValidate`, so this reaches the browser's own constraint validation and
   * blocks submit before Formik runs. The patient profile form wants that; the
   * creation forms render a blank row whether or not a number is being
   * entered, so they pass `false`.
   */
  required?: boolean;
}

export default function PhoneNumbersField({
  isEditing,
  name = 'phones',
  fullWidth,
  required = true,
}: PhoneNumbersFieldProps) {
  const { formatMessage } = useIntl();
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const phones: PhoneFormEntry[] = getIn(values, name) ?? [];
  const phoneUseOptions = PHONE_USES.map((use) => ({
    k: use,
    v: formatMessage(phoneUseMessages[use]),
  }));

  if (!isEditing) {
    return (
      <Box sx={{ width: '100%' }}>
        <InputLabel label={formatMessage(messages.phone)} />
        {phones.length === 0 ? (
          <Typography
            className="field-value"
            data-testid="drilldown-item-description"
            sx={{ fontSize: '16px', width: '100%', wordBreak: 'break-word' }}
          >
            -
          </Typography>
        ) : (
          phones.map((entry) => (
            <Typography
              className="field-value"
              data-testid="phone-entry"
              key={entry.id}
              sx={{ fontSize: '16px', width: '100%', wordBreak: 'break-word' }}
            >
              {formatMessage(
                entry.isPrimary
                  ? messages.readOnlyPrimaryEntry
                  : messages.readOnlyEntry,
                {
                  number: formatPhone(entry.value),
                  type: formatMessage(phoneUseMessages[entry.use]),
                }
              )}
            </Typography>
          ))
        )}
      </Box>
    );
  }

  const primaryIndex = phones.findIndex((entry) => entry.isPrimary);

  function setPrimaryIndex(index: number) {
    setFieldValue(
      name,
      phones.map((entry, i) => ({ ...entry, isPrimary: i === index }))
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={0.5}>
        <Typography color="text.secondary" variant="subtitle2">
          {phones.length > 1
            ? formatMessage(messages.phoneNumbers)
            : formatMessage(messages.phone)}
        </Typography>
        <FieldArray name={name}>
          {({ push }) => {
            const hasMultiple = phones.length > 1;

            const rows = phones.map((entry: PhoneFormEntry, index: number) => (
              <Stack key={entry.id} spacing={0.25}>
                <Stack alignItems="flex-end" direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <FormikTelField
                      fullWidth={fullWidth}
                      label={
                        hasMultiple
                          ? formatMessage(messages.phoneNumberWithPosition, {
                              position: index + 1,
                            })
                          : formatMessage(messages.phone)
                      }
                      name={`${name}[${index}].value`}
                      required={required}
                    />
                  </Box>
                  <Box sx={{ flexShrink: 0, width: 110 }}>
                    <FormikDropdownField
                      ariaLabel={
                        hasMultiple
                          ? formatMessage(messages.phoneTypeWithPosition, {
                              position: index + 1,
                            })
                          : formatMessage(messages.phoneType)
                      }
                      name={`${name}[${index}].use`}
                      onChange={(value) =>
                        setFieldValue(`${name}[${index}].use`, value)
                      }
                      options={phoneUseOptions}
                    />
                  </Box>
                  {hasMultiple && (
                    <IconButton
                      name="xmark"
                      iconType="fas"
                      data-testid={`remove-${name}-${index}`}
                      onClick={() => {
                        // Computed from this render's `phones` and applied
                        // in one setFieldValue call — chaining a separate
                        // FieldArray.remove() with a follow-up
                        // setFieldValue would race, since the second call's
                        // closure still sees the pre-removal array.
                        const newPrimaryIndex =
                          index <= primaryIndex
                            ? Math.max(0, primaryIndex - 1)
                            : primaryIndex;
                        const newPhones = phones
                          .filter((_, i) => i !== index)
                          .map((entry, i) => ({
                            ...entry,
                            isPrimary: i === newPrimaryIndex,
                          }));
                        setFieldValue(name, newPhones);
                      }}
                      tooltip={formatMessage(messages.removeNumber)}
                    />
                  )}
                </Stack>
                {hasMultiple && (
                  <FormControlLabel
                    control={
                      <Radio
                        inputProps={
                          {
                            'data-testid': `primary-radio-${name}-${index}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography color="text.secondary" variant="body2">
                        {formatMessage(messages.primaryNumber, {
                          position: index + 1,
                        })}
                      </Typography>
                    }
                    value={index}
                  />
                )}
              </Stack>
            ));

            return (
              <Stack spacing={1}>
                {hasMultiple ? (
                  <RadioGroup
                    aria-label={formatMessage(
                      messages.primaryNumberGroupAriaLabel
                    )}
                    name={`${name}-primary`}
                    onChange={(event) =>
                      setPrimaryIndex(Number(event.target.value))
                    }
                    value={primaryIndex}
                  >
                    <Stack spacing={1}>{rows}</Stack>
                  </RadioGroup>
                ) : (
                  rows
                )}
                <Button
                  onClick={() =>
                    push({
                      id: `new-${nextEntryId++}`,
                      value: '',
                      // The second number defaults to Home per VEG-779; the
                      // first and any beyond the second default to Mobile.
                      use: phones.length === 1 ? 'home' : 'mobile',
                      isPrimary: phones.length === 0,
                    })
                  }
                  size="small"
                  startIcon={<Icon color="inherit" name="plus" type="fas" />}
                  sx={{ alignSelf: 'flex-start' }}
                  variant="text"
                >
                  {formatMessage(messages.addNumber)}
                </Button>
              </Stack>
            );
          }}
        </FieldArray>
      </Stack>
    </Box>
  );
}
